import { useState, useEffect, useCallback } from "react";
import { Trash2, Undo2, Redo2, X, Magnet } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDrawingStore, type DrawingToolId } from "@/stores/drawingStore";
import {
  DRAWING_TOOLS, DRAWING_TOOL_SHORTCUT_KEYS, TOOL_LABELS,
} from "@/lib/lytick/drawingToolDefinitions";
import { clearAllDrawings } from "@/lib/lytick/overlay-bridge";
import {
  canRedoDrawingAction, canUndoDrawingAction,
  redoLastDrawingAction, undoLastDrawingAction,
} from "@/lib/lytick/overlay-bridge";
import { getDrawingToolIconComponent } from "@/components/icons/drawing-icons";

// ── Tool group definitions ──────────────────────────────────────────────────

const LINE_IDS_GEO = ["trendLine", "horizontalLine", "verticalLine", "ray"];
const LINE_IDS_PRICE = ["priceLine", "parallelChannel"];
const ALL_LINE_IDS = [...LINE_IDS_GEO, ...LINE_IDS_PRICE];

const SHAPES_ANNO_IDS = ["rectangle", "circle", "fibonacci", "textAnnotation", "textTag"];
const MEASURE_IDS = ["measure", "riskReward"];

// ── Color presets ───────────────────────────────────────────────────────────

const RAIL_COLORS = [
  "#FFFFFF", "#F5A623", "#0EC4B4", "#16B85A",
  "#F04545", "#F07848", "#9D7FEA", "#5DC8E8",
  "#8FA8C8", "#94A3B8", "#475569", "#1E293B",
];

// ── Shared style fragments ──────────────────────────────────────────────────

const KBD_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontFamily: "var(--font-mono)",
  backgroundColor: "rgba(255,255,255,0.08)",
  color: "var(--lyt-text-muted)",
  padding: "1px 5px",
  borderRadius: 3,
  border: "1px solid rgba(255,255,255,0.08)",
  lineHeight: "16px",
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getToolDef(toolId: string) {
  return DRAWING_TOOLS.find((t) => t.id === toolId);
}

function RailDivider() {
  return (
    <div
      aria-hidden="true"
      style={{ height: 1, backgroundColor: "var(--lyt-border-subtle)", margin: "4px 8px", alignSelf: "stretch" }}
    />
  );
}

// ── Tool icon renderer (strokeWidth on Lucide icons via className trick) ────

function ToolIcon({ toolId, className = "size-[18px]" }: { toolId: string; className?: string }) {
  const Icon = getDrawingToolIconComponent(toolId);
  return <Icon className={className} />;
}

// ── Rail icon button with tooltip ───────────────────────────────────────────

interface RailButtonProps {
  toolId: string;
  label: string;
  active: boolean;
  shortcut?: string;
  onClick: () => void;
}

function RailButton({ toolId, label, active, shortcut, onClick }: RailButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-pressed={active}
          className="relative flex items-center justify-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: "var(--radius-md)",
            color: active ? "var(--lyt-accent)" : "var(--lyt-text-muted)",
            backgroundColor: active ? "var(--lyt-accent-dim)" : "transparent",
            transition: "color 0.12s ease, background-color 0.12s ease",
            cursor: "pointer",
            border: "none",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            if (!active) {
              e.currentTarget.style.color = "var(--lyt-text-secondary)";
              e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
            }
          }}
          onMouseLeave={(e) => {
            if (!active) {
              e.currentTarget.style.color = "var(--lyt-text-muted)";
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          {/* Left accent bar — flush to the rail strip's left edge (button left-inset is 8px) */}
          {active && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: -8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 3,
                height: 18,
                backgroundColor: "var(--lyt-accent)",
                borderRadius: "0 3px 3px 0",
              }}
            />
          )}
          <ToolIcon toolId={toolId} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 500 }}>{label}</span>
          {shortcut && <kbd style={KBD_STYLE}>{shortcut}</kbd>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// ── Submenu item ─────────────────────────────────────────────────────────────

interface SubmenuItemProps {
  toolId: string;
  label: string;
  active: boolean;
  shortcut?: string;
  onSelect: () => void;
}

function SubmenuItem({ toolId, label, active, shortcut, onSelect }: SubmenuItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={label}
      style={{
        width: "100%",
        height: 40,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 16px",
        cursor: "pointer",
        border: "none",
        borderLeft: active ? "3px solid var(--lyt-accent)" : "3px solid transparent",
        backgroundColor: active ? "var(--lyt-accent-dim)" : "transparent",
        color: active ? "var(--lyt-accent)" : "var(--lyt-text-muted)",
        transition: "background-color 0.1s, color 0.1s",
        fontFamily: "var(--font-ui)",
        textAlign: "left",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
          e.currentTarget.style.color = "var(--lyt-text-secondary)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "var(--lyt-text-muted)";
        }
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 6,
          backgroundColor: active ? "var(--lyt-accent-dim)" : "var(--surface-tint)",
          flexShrink: 0,
          color: active ? "var(--lyt-accent)" : "var(--lyt-text-muted)",
        }}
      >
        <ToolIcon toolId={toolId} className="size-[14px]" />
      </div>
      <span
        style={{
          flex: 1,
          fontSize: 12.5,
          fontWeight: active ? 500 : 400,
          color: active ? "var(--lyt-accent)" : "var(--lyt-text-secondary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {shortcut && (
        <kbd
          style={{
            fontSize: 9.5,
            fontFamily: "var(--font-mono)",
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "var(--lyt-text-muted)",
            padding: "1px 4px",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.06)",
            lineHeight: "14px",
            flexShrink: 0,
          }}
        >
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

// ── Lines submenu panel ──────────────────────────────────────────────────────

interface LinesSubmenuProps {
  open: boolean;
  activeTool: DrawingToolId | null;
  lastLineTool: string;
  onSelectTool: (toolId: DrawingToolId) => void;
  onClose: () => void;
}

function LinesSubmenu({ open, activeTool, lastLineTool, onSelectTool, onClose }: LinesSubmenuProps) {
  const displayToolId = (activeTool && ALL_LINE_IDS.includes(activeTool)) ? activeTool : lastLineTool;
  const displayDef = getToolDef(displayToolId);

  return (
    // Outer reserves flex width to push chart
    <div
      style={{
        width: open ? 196 : 0,
        height: "100%",
        overflow: "hidden",
        transition: "width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        flexShrink: 0,
        borderRight: "1px solid var(--lyt-border-subtle)",
        backgroundColor: "var(--lyt-bg-surface)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Inner: fixed 196px so content doesn't squish during width transition */}
      <div
        style={{
          width: 196,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(-12px)",
          opacity: open ? 1 : 0,
          transition: "transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.15s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px 16px 9px",
            borderBottom: "1px solid var(--lyt-border-subtle)",
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase" as const,
              letterSpacing: "0.08em",
              color: "var(--lyt-text-muted)",
              fontFamily: "var(--font-ui)",
            }}
          >
            Line Tools
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close line tools panel"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 20,
              height: 20,
              borderRadius: 4,
              color: "var(--lyt-text-muted)",
              backgroundColor: "transparent",
              cursor: "pointer",
              border: "none",
              padding: 0,
              transition: "color 0.1s, background 0.1s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--lyt-text-secondary)";
              e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--lyt-text-muted)";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <X size={12} strokeWidth={2.2} />
          </button>
        </div>

        {/* Tool items */}
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 4, paddingBottom: 4 }}>
          {LINE_IDS_GEO.map((toolId) => {
            const def = getToolDef(toolId);
            if (!def) return null;
            return (
              <SubmenuItem
                key={toolId}
                toolId={toolId}
                label={def.label}
                active={activeTool === toolId}
                shortcut={DRAWING_TOOL_SHORTCUT_KEYS[toolId]}
                onSelect={() => onSelectTool(toolId as DrawingToolId)}
              />
            );
          })}

          {/* Sub-divider: geometric lines / price tools */}
          <div
            aria-hidden="true"
            style={{
              height: 1,
              backgroundColor: "var(--lyt-border-subtle)",
              margin: "4px 16px",
            }}
          />

          {LINE_IDS_PRICE.map((toolId) => {
            const def = getToolDef(toolId);
            if (!def) return null;
            return (
              <SubmenuItem
                key={toolId}
                toolId={toolId}
                label={def.label}
                active={activeTool === toolId}
                shortcut={DRAWING_TOOL_SHORTCUT_KEYS[toolId]}
                onSelect={() => onSelectTool(toolId as DrawingToolId)}
              />
            );
          })}
        </div>

        {/* Active tool footer badge */}
        {displayDef && (
          <div
            style={{
              borderTop: "1px solid var(--lyt-border-subtle)",
              padding: "8px 16px 10px",
            }}
          >
            <p
              style={{
                fontSize: 9,
                textTransform: "uppercase" as const,
                letterSpacing: "0.06em",
                color: "var(--lyt-text-muted)",
                marginBottom: 5,
                fontFamily: "var(--font-ui)",
              }}
            >
              Active tool
            </p>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                backgroundColor: "var(--lyt-accent-dim)",
                borderRadius: "var(--radius-full)",
                padding: "3px 8px",
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "var(--lyt-accent)",
                  flexShrink: 0,
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: "var(--lyt-accent)",
                  fontFamily: "var(--font-ui)",
                  fontWeight: 500,
                }}
              >
                {displayDef.label}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function DrawingToolRail() {
  const {
    activeTool,
    snapMode,
    drawings,
    drawingColor,
    setActiveTool,
    toggleSnapMode,
    setDrawingColor,
    addRecentColor,
  } = useDrawingStore();

  const [linesOpen, setLinesOpen] = useState(false);
  const [lastLineTool, setLastLineTool] = useState("trendLine");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // Sync undo/redo stack availability
  useEffect(() => {
    const sync = () => {
      setCanUndo(canUndoDrawingAction());
      setCanRedo(canRedoDrawingAction());
    };
    sync();
    window.addEventListener("lytick:drawing-history-changed", sync);
    return () => window.removeEventListener("lytick:drawing-history-changed", sync);
  }, []);

  // Track last used line tool; close submenu when a non-line tool is activated
  useEffect(() => {
    if (!activeTool) return;
    if (ALL_LINE_IDS.includes(activeTool)) {
      setLastLineTool(activeTool);
    } else {
      setLinesOpen(false);
    }
  }, [activeTool]);

  // Keyboard: Escape closes submenu or clears tool; Alt+M toggles snap
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && e.key === "m") {
        e.preventDefault();
        toggleSnapMode();
      }
      if (e.key === "Escape") {
        if (linesOpen) {
          e.preventDefault();
          setLinesOpen(false);
        } else if (activeTool) {
          e.preventDefault();
          setActiveTool(null);
          setAnnouncement("Tool deactivated");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleSnapMode, activeTool, setActiveTool, linesOpen]);

  const handleSelectTool = useCallback((toolId: DrawingToolId) => {
    const next = activeTool === toolId ? null : toolId;
    setActiveTool(next);
    setAnnouncement(next ? `${TOOL_LABELS[toolId] ?? toolId} active` : "Tool deactivated");
  }, [activeTool, setActiveTool]);

  const handleSelectLineTool = useCallback((toolId: DrawingToolId) => {
    setActiveTool(toolId);
    setAnnouncement(`${TOOL_LABELS[toolId] ?? toolId} active`);
  }, [setActiveTool]);

  const handleColorSelect = useCallback((color: string) => {
    setDrawingColor(color);
    addRecentColor(color);
  }, [setDrawingColor, addRecentColor]);

  const isLineActive = activeTool !== null && ALL_LINE_IDS.includes(activeTool);
  const linesDisplayToolId = isLineActive ? activeTool! : lastLineTool;
  const linesDisplayDef = getToolDef(linesDisplayToolId);

  return (
    <TooltipProvider delayDuration={300}>
      {/* Screen-reader announcer */}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </span>

      <div
        id="onboarding-drawings"
        className="flex h-full select-none"
        style={{ zIndex: "var(--z-toolbar)" as never }}
      >
        {/* ── Rail Strip (52px) ─────────────────────────────────────── */}
        <div
          className="flex flex-col h-full shrink-0"
          style={{
            width: 52,
            backgroundColor: "var(--lyt-bg-surface)",
            borderRight: "1px solid var(--lyt-border-subtle)",
          }}
        >
          {/* Tool groups — scrollable */}
          <div className="flex-1 flex flex-col items-center py-2 overflow-y-auto overflow-x-hidden gap-0.5">

            {/* Group 1: Lines (opens submenu) */}
            <RailButton
              toolId={linesDisplayToolId}
              label={linesDisplayDef?.label ?? "Line Tool"}
              active={isLineActive}
              shortcut={DRAWING_TOOL_SHORTCUT_KEYS[linesDisplayToolId]}
              onClick={() => setLinesOpen((prev) => !prev)}
            />

            <RailDivider />

            {/* Group 2: Shapes & Annotations */}
            {SHAPES_ANNO_IDS.map((toolId) => {
              const def = getToolDef(toolId);
              if (!def) return null;
              return (
                <RailButton
                  key={toolId}
                  toolId={toolId}
                  label={def.label}
                  active={activeTool === toolId}
                  shortcut={DRAWING_TOOL_SHORTCUT_KEYS[toolId]}
                  onClick={() => handleSelectTool(toolId as DrawingToolId)}
                />
              );
            })}

            <RailDivider />

            {/* Group 3: Measure & Analyze */}
            {MEASURE_IDS.map((toolId) => {
              const def = getToolDef(toolId);
              if (!def) return null;
              return (
                <RailButton
                  key={toolId}
                  toolId={toolId}
                  label={def.label}
                  active={activeTool === toolId}
                  shortcut={DRAWING_TOOL_SHORTCUT_KEYS[toolId]}
                  onClick={() => handleSelectTool(toolId as DrawingToolId)}
                />
              );
            })}

            <RailDivider />

            {/* Group 4: Snap / Magnet */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleSnapMode}
                  aria-label={`Magnet mode ${snapMode ? "on" : "off"}`}
                  aria-pressed={snapMode}
                  className="flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "var(--radius-md)",
                    color: snapMode ? "var(--lyt-info, #60A5FA)" : "var(--lyt-text-muted)",
                    backgroundColor: snapMode ? "rgba(96,165,250,0.10)" : "transparent",
                    transition: "color 0.12s ease, background-color 0.12s ease",
                    cursor: "pointer",
                    border: "none",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    if (!snapMode) {
                      e.currentTarget.style.color = "var(--lyt-text-secondary)";
                      e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!snapMode) {
                      e.currentTarget.style.color = "var(--lyt-text-muted)";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <Magnet
                    className="size-[18px]"
                    strokeWidth={1.6}
                    style={{ fill: snapMode ? "rgba(96,165,250,0.25)" : "none" }}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 500 }}>Magnet Mode</span>
                  <kbd style={KBD_STYLE}>Alt+M</kbd>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* ── Bottom utility: color · undo · redo · clear ───────────── */}
          <div
            className="flex flex-col items-center gap-1 py-2"
            style={{ borderTop: "1px solid var(--lyt-border-subtle)" }}
          >
            {/* Color swatch */}
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Drawing color"
                      className="flex items-center justify-center"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "var(--radius-md)",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        border: "none",
                        transition: "background-color 0.12s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {/* Swatch circle with ring gap */}
                      <span
                        style={{
                          display: "inline-block",
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: drawingColor,
                          // ring: 2px gap + 1.5px ring at 35% opacity of the swatch color
                          boxShadow: `0 0 0 2px var(--lyt-bg-surface), 0 0 0 3.5px ${drawingColor}59`,
                          flexShrink: 0,
                        }}
                      />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>Drawing color</TooltipContent>
              </Tooltip>
              <PopoverContent
                side="right"
                align="end"
                sideOffset={8}
                style={{
                  backgroundColor: "var(--lyt-bg-elevated)",
                  borderColor: "var(--lyt-border-default)",
                  borderRadius: "var(--radius-lg)",
                  padding: 12,
                  width: "auto",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 20px)",
                    gap: 6,
                  }}
                >
                  {RAIL_COLORS.map((color) => {
                    const isSelected = drawingColor.toUpperCase() === color.toUpperCase();
                    return (
                      <button
                        key={color}
                        type="button"
                        aria-label={`Color ${color}`}
                        aria-pressed={isSelected}
                        onClick={() => handleColorSelect(color)}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "var(--radius-sm)",
                          backgroundColor: color,
                          cursor: "pointer",
                          border: "none",
                          outline: isSelected
                            ? "2px solid #FFFFFF"
                            : "1px solid rgba(255,255,255,0.12)",
                          outlineOffset: isSelected ? 1.5 : 0,
                          transition: "outline 0.1s, transform 0.1s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.transform = "scale(1.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* Undo */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Undo"
                  disabled={!canUndo}
                  onClick={canUndo ? undoLastDrawingAction : undefined}
                  className="flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "var(--radius-md)",
                    color: "var(--lyt-text-muted)",
                    backgroundColor: "transparent",
                    cursor: canUndo ? "pointer" : "not-allowed",
                    opacity: canUndo ? 1 : 0.3,
                    border: "none",
                    transition: "background-color 0.12s, opacity 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (canUndo) e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Undo2 className="size-[18px]" strokeWidth={1.6} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 500 }}>Undo</span>
                  <kbd style={KBD_STYLE}>Ctrl+Z</kbd>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Redo */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Redo"
                  disabled={!canRedo}
                  onClick={canRedo ? redoLastDrawingAction : undefined}
                  className="flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "var(--radius-md)",
                    color: "var(--lyt-text-muted)",
                    backgroundColor: "transparent",
                    cursor: canRedo ? "pointer" : "not-allowed",
                    opacity: canRedo ? 1 : 0.3,
                    border: "none",
                    transition: "background-color 0.12s, opacity 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (canRedo) e.currentTarget.style.backgroundColor = "var(--surface-tint-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Redo2 className="size-[18px]" strokeWidth={1.6} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 500 }}>Redo</span>
                  <kbd style={KBD_STYLE}>Ctrl+Y</kbd>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Clear all drawings */}
            {drawings.length > 0 && (
              <AlertDialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Clear all drawings (${drawings.length})`}
                        className="relative flex items-center justify-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "var(--radius-md)",
                          color: "var(--lyt-text-muted)",
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          border: "none",
                          transition: "background-color 0.12s, color 0.12s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "var(--lyt-loss)";
                          e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "var(--lyt-text-muted)";
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <Trash2 className="size-[18px]" strokeWidth={1.6} />
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            minWidth: 14,
                            height: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "var(--radius-full)",
                            padding: "0 2px",
                            fontSize: 9,
                            lineHeight: 1,
                            fontFamily: "var(--font-mono)",
                            backgroundColor: "var(--lyt-accent)",
                            color: "var(--text-on-accent-bright, #032E2A)",
                          }}
                        >
                          {drawings.length}
                        </span>
                      </button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    Clear All ({drawings.length})
                  </TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear all drawings?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove all {drawings.length} drawing{drawings.length !== 1 ? "s" : ""} from the chart. This action can be undone with Undo.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={clearAllDrawings}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Clear all
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* ── Lines Submenu Panel ───────────────────────────────────── */}
        <LinesSubmenu
          open={linesOpen}
          activeTool={activeTool}
          lastLineTool={lastLineTool}
          onSelectTool={handleSelectLineTool}
          onClose={() => setLinesOpen(false)}
        />
      </div>
    </TooltipProvider>
  );
}
